// skipcq: JS-W1028
const DEFAULT_LIST_LIMIT = 3;

/**
 * @param {unknown} value
 * @returns {number | null}
 */
function toMillis(value) {
  if (value == null) {
    return null;
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (value instanceof Date) {
    return value.getTime();
  }
  if (typeof value === 'object' && value !== null && typeof value.toDate === 'function') {
    const parsed = value.toDate();
    if (parsed instanceof Date && !Number.isNaN(parsed.getTime())) {
      return parsed.getTime();
    }
  }
  if (typeof value === 'object' && value !== null && typeof value.seconds === 'number') {
    return value.seconds * 1000;
  }
  return null;
}

/**
 * @param {unknown} value
 * @returns {number | null}
 */
function toOverallRating(value) {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return null;
  }
  return value;
}

/**
 * @param {Array<{ name?: string, createdAt?: unknown }>} beers
 * @param {number} [limit]
 * @returns {Array<{ name?: string, createdAt?: unknown }>}
 */
export function selectRecentlyAddedBeers(beers, limit = DEFAULT_LIST_LIMIT) {
  if (!Array.isArray(beers) || beers.length === 0) {
    return [];
  }

  const sorted = [...beers].sort((beerA, beerB) => {
    const millisA = toMillis(beerA.createdAt);
    const millisB = toMillis(beerB.createdAt);
    if (millisA === null && millisB === null) {
      return 0;
    }
    if (millisA === null) {
      return 1;
    }
    if (millisB === null) {
      return -1;
    }
    return millisB - millisA;
  });

  return sorted.slice(0, limit);
}

/**
 * @param {Array<{ name?: string, ratings?: { overall?: unknown } }>} beers
 * @param {number} [limit]
 * @returns {Array<{ name?: string, ratings?: { overall?: unknown } }>}
 */
export function selectTopRatedBeers(beers, limit = DEFAULT_LIST_LIMIT) {
  if (!Array.isArray(beers) || beers.length === 0) {
    return [];
  }

  const rated = beers.filter((beer) => toOverallRating(beer.ratings?.overall) !== null);

  const sorted = [...rated].sort((beerA, beerB) => {
    const ratingA = toOverallRating(beerA.ratings?.overall) ?? 0;
    const ratingB = toOverallRating(beerB.ratings?.overall) ?? 0;
    if (ratingB !== ratingA) {
      return ratingB - ratingA;
    }
    const nameA = (beerA.name || '').toLowerCase();
    const nameB = (beerB.name || '').toLowerCase();
    return nameA.localeCompare(nameB);
  });

  return sorted.slice(0, limit);
}

/**
 * @param {unknown} value
 * @returns {number}
 */
function resolveTagCount(value) {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  return 0;
}

/**
 * @param {Record<string, Array<{ tag?: string, count?: number }>> | null | undefined} beerTags
 * @param {Record<string, string>} tagLabelMap
 * @param {string} category
 * @returns {Array<{ label: string, count: number }>}
 */
export function getBeerTagLabelsForCategory(beerTags, tagLabelMap, category) {
  if (
    !beerTags
    || typeof beerTags !== 'object'
    || !tagLabelMap
    || typeof category !== 'string'
    || category.length === 0
  ) {
    return [];
  }

  const tagCounts = beerTags[category];
  if (!Array.isArray(tagCounts)) {
    return [];
  }

  const withLabels = [];

  tagCounts.forEach((tagCount) => {
    const tagId = typeof tagCount?.tag === 'string' ? tagCount.tag : '';
    const count = resolveTagCount(tagCount?.count);
    const label = tagLabelMap[tagId];
    if (tagId.length > 0 && label && label.length > 0) {
      withLabels.push({ label, count });
    }
  });

  withLabels.sort((entryA, entryB) => {
    if (entryB.count !== entryA.count) {
      return entryB.count - entryA.count;
    }
    return entryA.label.localeCompare(entryB.label);
  });

  return withLabels;
}

/**
 * @param {Record<string, string[]> | null | undefined} reviewTags
 * @param {Record<string, string>} tagLabelMap
 * @param {string} category
 * @returns {Array<{ label: string, count: number }>}
 */
export function getReviewTagLabelsForCategory(reviewTags, tagLabelMap, category) {
  if (
    !reviewTags
    || typeof reviewTags !== 'object'
    || !tagLabelMap
    || typeof category !== 'string'
    || category.length === 0
  ) {
    return [];
  }

  const tagIds = reviewTags[category];
  if (!Array.isArray(tagIds)) {
    return [];
  }

  const withLabels = [];

  tagIds.forEach((tagId) => {
    if (typeof tagId !== 'string' || tagId.length === 0) {
      return;
    }
    const label = tagLabelMap[tagId];
    if (typeof label === 'string' && label.length > 0) {
      withLabels.push({ label, count: 1 });
    }
  });

  withLabels.sort((entryA, entryB) => entryA.label.localeCompare(entryB.label));

  return withLabels;
}

/**
 * @param {Record<string, Array<{ tag?: string, count?: number }>> | null | undefined} beerTags
 * @param {Record<string, string>} tagLabelMap
 * @param {number} [limit]
 * @returns {Array<{ label: string, count: number }>}
 */
export function getTopBeerTagLabels(beerTags, tagLabelMap, limit = DEFAULT_LIST_LIMIT) {
  if (!beerTags || typeof beerTags !== 'object' || !tagLabelMap) {
    return [];
  }

  const withLabels = [];

  Object.values(beerTags).forEach((tagCounts) => {
    if (!Array.isArray(tagCounts)) {
      return;
    }
    tagCounts.forEach((tagCount) => {
      const tagId = typeof tagCount?.tag === 'string' ? tagCount.tag : '';
      const count = resolveTagCount(tagCount?.count);
      const label = tagLabelMap[tagId];
      if (tagId.length > 0 && label && label.length > 0) {
        withLabels.push({ label, count });
      }
    });
  });

  withLabels.sort((entryA, entryB) => {
    if (entryB.count !== entryA.count) {
      return entryB.count - entryA.count;
    }
    return entryA.label.localeCompare(entryB.label);
  });

  const seen = new Set();
  const results = [];
  withLabels.forEach((entry) => {
    if (seen.has(entry.label) || results.length >= limit) {
      return;
    }
    seen.add(entry.label);
    results.push({ label: entry.label, count: entry.count });
  });

  return results;
}

/**
 * @param {Array<{ id?: string, name?: string }>} beerStyles
 * @returns {Record<string, string>}
 */
export function buildBeerStyleLabelMap(beerStyles) {
  const labelMap = {};
  if (!Array.isArray(beerStyles)) {
    return labelMap;
  }

  beerStyles.forEach((styleRow) => {
    if (styleRow == null || typeof styleRow !== 'object') {
      return;
    }
    const styleId = typeof styleRow.id === 'string' ? styleRow.id : '';
    if (styleId.length === 0) {
      return;
    }
    const styleName = typeof styleRow.name === 'string' ? styleRow.name.trim() : '';
    labelMap[styleId] = styleName.length > 0 ? styleName : styleId;
  });

  return labelMap;
}

/**
 * @param {unknown} styleId
 * @param {Record<string, string>} styleLabelMap
 * @returns {string}
 */
export function resolveBeerStyleLabel(styleId, styleLabelMap) {
  if (typeof styleId !== 'string' || styleId.length === 0) {
    return '—';
  }
  const label = styleLabelMap?.[styleId];
  if (typeof label === 'string' && label.length > 0) {
    return label;
  }
  return '—';
}

/**
 * @param {unknown} rating
 * @returns {string}
 */
export function formatBeerRating(rating) {
  const numeric = toOverallRating(rating);
  if (numeric === null) {
    return '—';
  }
  return `${numeric.toFixed(1)} ★`;
}
